import { getVersions } from '@shared/helpers/getVersions';
import { handleDataClear } from '../helpers/handleDataClear';
import { getValidatedBase64Cookie } from '@shared/cookies/getValidatedBase64Cookie';
import { consoleLogger } from '@shared/logging/consoleLogger';
import { UtiqAskMeLater, UtiqStatusEntryName } from '@shared/constants/utiq';
import { createCookie } from '@shared/cookies/createCookie';
import { encodeToBase64 } from '@shared/conversion/encodeToBase64';
import { getBaseDomain } from '@shared/domain/getBaseDomain';
import { utiqMachine } from '@machine/index';
import {
  addEventListener,
  removeEventListener,
} from '@shared/api/sharedAPIEventFunctions';
import { callEventListeners } from '@shared/events/callEventListeners';
import { UtiqEvents } from '@shared/events/events';

export const initializeUtiqApiFunctions = (
  environment,
  handleRevocationPopupCallback,
  handleConsentWithModalCallback,
) => {
  /**
   * API method to handle consent cookie entry and event dispatch
   * @param isConsentGranted - consent status to update
   * @param options - syncConsentOnAccept flag should be only set to true when the consent is explicitly accepted
   */
  const handleConsentChange = (
    isConsentGranted,
    options = { syncConsentOnAccept: false, clearConsent: false },
  ) => {
    if (options?.syncConsentOnAccept) {
      window.Utiq.syncVersionCookies = true;
    }
    if (typeof isConsentGranted !== 'boolean') {
      consoleLogger.logError(
        `Incorrect consent value provided for Utiq handleConsentChange: ${isConsentGranted}`,
      );
      return;
    }

    if (!isConsentGranted && options?.clearConsent) {
      const clearConsent = () => {
        consoleLogger.logDebug(
          'Setting Utiq consent to undefined as clearConsent option was requested with handleConsentChange API call...',
        );
        window.document.cookie = createCookie({
          name: UtiqStatusEntryName,
          value: encodeToBase64(false),
          days: 0,
          domain: getBaseDomain(window.location.hostname),
        });
        window.Utiq.API.removeEventListener(
          UtiqEvents.onConsentUpdateFinished.name,
          clearConsent,
        );
      };
      window.Utiq.API.addEventListener(
        UtiqEvents.onConsentUpdateFinished.name,
        clearConsent,
      );
    }
    callEventListeners('onConsentChanging', 'Utiq', { isConsentGranted });
  };

  /* API method to handle clearing all the Utiq-related data */
  const handleDataClearFunction = () => {
    handleDataClear(environment);
  };

  /* API method to get Utiq consent status */
  const getUtiqConsentStatus = () => {
    const currentConsentStatusCookie = getValidatedBase64Cookie(
      document.cookie,
      UtiqStatusEntryName,
    );
    if (currentConsentStatusCookie) {
      return currentConsentStatusCookie === 'true';
    }

    return undefined;
  };

  /* API method to handle consent change to reject with revocation popup */
  const revokeConsentWithPopup = async () => {
    const showRevocationPopup = handleRevocationPopupCallback;
    const currentConsentStatusCookie = getValidatedBase64Cookie(
      document.cookie,
      UtiqStatusEntryName,
    );

    if (!currentConsentStatusCookie || currentConsentStatusCookie === 'false') {
      if (!window.Utiq?.data?.showRevocationErrorMessage) {
        window.Utiq ||= {};
        window.Utiq.data ||= {};
        window.Utiq.data.showRevocationErrorMessage = true;
        localStorage.removeItem(UtiqAskMeLater);
        sessionStorage.removeItem(UtiqAskMeLater);
      }
      showRevocationPopup();
    } else {
      utiqMachine.ctx.times.consentExternalyDenied = new Date(Date.now());

      if (!window.Utiq?.data?.popupContent?.version) return;
      if (!window.Utiq?.data?.popupContent?.versionPrevious) return;

      const consentVersion = getVersions(
        window.Utiq?.data?.popupContent?.version,
      ).majorVersion;

      if (!consentVersion) return;
      callEventListeners('onConsentChanging', 'Utiq', {
        isConsentGranted: false,
      });
      const showRevocationPopupHandler = (evt) => {
        const { isConsentGranted } = evt;
        if (isConsentGranted === false) {
          showRevocationPopup();
        }
      };
      addEventListener('onConsentUpdateFinished', (evt) => {
        showRevocationPopupHandler(evt);
        removeEventListener(
          'onConsentUpdateFinished',
          showRevocationPopupHandler,
        );
      });
    }
  };

  window.Utiq ||= {};
  window.Utiq.API ||= {};
  window.Utiq.API.handleConsentChange = handleConsentChange;
  window.Utiq.API.handleDataClear = handleDataClearFunction;
  window.Utiq.API.getUtiqConsentStatus = getUtiqConsentStatus;
  window.Utiq.API.revokeConsentWithPopup = revokeConsentWithPopup;
  window.Utiq.API.showConsentModal = handleConsentWithModalCallback;
  if (!('getIdGraphEntry' in window.Utiq.API))
    window.Utiq.API.getIdGraphEntry = () => {
      consoleLogger.logInfo(
        'User has not consented, getIdGraphEntry is not available',
      );
    };

  consoleLogger.logDebug('Utiq functions initialized...');
};
